import moment from 'moment';
import { Injectable } from '@angular/core';
import { OfflineTutorialsPage } from '@app/offline-tutorials/offline-tutorials.page';
import { LocalNotifications } from '@awesome-cordova-plugins/local-notifications/ngx';
import { ModalController } from '@ionic/angular';
import { UserStoreService } from '@services/api/user-store/user-store.service';
import { StorageService } from '@services/storage/storage.service';
import { environment } from 'src/environments/environment';
import { StatisticCachingService } from '@services/api/caching/statistic-caching/statistic-caching.service';
import { AnalyticsService } from '@services/analytics/analytics.service';
import { AnalyticsEventEnum } from '@models/AnalyticsEventEnum';

const ONBOARDING_NOTIFACTION_STATE_KEY = 'ONBOARDING_NOTIFICAION_STATE';
const ONBOARDING_NOTIFICAITON_GIFTED = 'ONBOARDING_NOTIFICAITON_GIFTED';

@Injectable({
  providedIn: 'root'
})
export class OnboardingNotificationSchedulerService {
  constructor(
    private modalCtrl: ModalController,
    private localNotifications: LocalNotifications,
    private userStoreService: UserStoreService,
    private storageService: StorageService,
    private statisticCachingService: StatisticCachingService,
    private analyticsService: AnalyticsService
  ) {}

  init() {
    const userCreateDate = moment(this.userStoreService.user.createdAt).toDate();
    this.scheduleNotifications(userCreateDate);
  }

  async scheduleNotifications(userCreatedAt: Date) {
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - userCreatedAt.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays > 30) {
      return;
    }

    //checar se ja foi criadas;
    let notifications = await this.fetchNotificationsState();
    const isNotificationCreated = notifications !== null && notifications.length > 0;

    if (isNotificationCreated === false) {
      const notificationsToCreate = this.createNotificationsFromUserCreatedAt(userCreatedAt);

      this.localNotifications.schedule(notificationsToCreate.filter((notification) => notification.trigger.at > now));

      const notificationsStateToCreate = notificationsToCreate.map((not) => ({
        id: not.id,
        scheduledAt: not.trigger.at,
        displayedAt: null
      }));

      await this.saveNotifcationState(notificationsStateToCreate);
    }
  }

  async shouldShowAnyModalNotification() {
    const notificationsState = await this.fetchNotificationsState();
    this.shouldShowEbookNotification();

    if (notificationsState) {
      const notificationStateNotShowed = notificationsState.filter((n) => n.displayedAt === null);

      if (notificationStateNotShowed.length > 0) {
        const today = new Date();
        const notificationStateShouldShow = notificationStateNotShowed.filter((n) =>
          this.isSameDayOrAfter(today, n.scheduledAt)
        );
        if (notificationStateShouldShow.length > 0) {
          const sample = notificationStateShouldShow[0];
          this.showModalByNotificationId(sample.id);
        }
      }
    }
  }

  async shouldShowEbookNotification() {
    const shouldBlockEbookNotification = await this.shouldBlockEbookNotification();
    if (shouldBlockEbookNotification) return;

    const statistics = await this.statisticCachingService.read();
    if (statistics.length >= 5) {
      this.showEbookModal();
    }
  }

  private async shouldBlockEbookNotification(): Promise<boolean> {
    const isOldAccount = this.hasAccountPassed30Days(this.userStoreService.user.createdAt);
    if (isOldAccount) return true;

    const alreadyGifted = await this.ebookWasGifted();
    return alreadyGifted;
  }

  private async fetchNotificationsState(): Promise<
    { id: number; scheduledAt: Date; displayedAt: Date | null }[] | null
  > {
    const notifications = await this.storageService.get(ONBOARDING_NOTIFACTION_STATE_KEY);
    return notifications;
  }

  private async saveNotifcationState(notifications: { id: number; scheduledAt: Date; displayedAt: Date | null }[]) {
    return this.storageService.set(ONBOARDING_NOTIFACTION_STATE_KEY, notifications);
  }

  async reviewModal() {
    const prop = {
      title: 'Já programou suas revisões ?',
      youtubeURL: 'https://youtu.be/b3luY3cpFRk',
      youtubeTextHTML: 'Veja como <br> revisar',
      message: `
                A revisão é a <chave> para <fixar o conhecimento> e evitar o esquecimento. 
                Quando você <revisa> um conteúdo de <forma estratégica>, reforça o aprendizado e melhora a retenção a longo prazo. 
                Com <revisões programadas>, você revisa no momento certo, garantindo mais eficiência nos estudos.
                No <Estudaqui>, você encontra a ferramenta mais <eficiente> para <gerenciar suas revisões> com praticidade e inteligência.
              `
    };
    this.openHelper(prop);
    this.analyticsService.logEvent(AnalyticsEventEnum.POPUP_MESSAGE_REVIEW);
  }

  async cycleOrScheduleModal() {
    const prop = {
      title: 'Ciclo ou Agenda ?',
      youtubeURL: 'https://youtu.be/5M9Bs8R7v8o',
      youtubeTextHTML: 'Saiba mais',
      message: `Escolha a melhor forma de criar seu plano de estudo:
                <Ciclo de estudo:> Conhecida entre os concurseiros, é uma boa opção para quem precisa de <flexibilidade>, permitindo estudar várias disciplinas ao longo da semana, sem uma rotina fixa.
                <Agenda:> Uma opção excelente para quem gosta de <organizar> o estudo por <dia da semana>, garantindo que cada dia tenha um objetivo claro e atingível.
                <Qual delas vai melhor te ajudar a alcançar seus objetivos ?>
        `
    };
    this.openHelper(prop);
    this.analyticsService.logEvent(AnalyticsEventEnum.POPUP_MESSAGE_STUDY_PLAN);
  }

  async firstWeek() {
    const prop = {
      title: '1ª semana no Estudaqui! ',
      youtubeURL: 'https://youtu.be/ihOTCQXnOM0?si=1Za53eiDnWLYvfJb',
      youtubeTextHTML: 'Saiba como acompanhar seus estudos',
      message: `
                Como foi sua experiência até agora?
                Estamos muito felizes que você começou a sua jornada de estudos conosco!
                <Lembre-se:> registrar e usar a ferramenta de forma contínua é essencial para alcançar seus objetivos.
                <Cada dia conta para o seu progresso!>
        `
    };
    this.openHelper(prop);
    this.analyticsService.logEvent(AnalyticsEventEnum.POPUP_MESSAGE_FIRST_WEEK);
  }

  async firstMonth() {
    // ALTERAR O LINK OFFER URL
    const urlString = this.mountOTOofferLink('popup_first_month');

    const prop = {
      title: '1º  MÊS no Estudaqui! ',
      offerURL: urlString,
      youtubeTextHTML: 'Aproveitar Oferta Única<br>20% OFF',
      message: `
            <Parabéns pelo primeiro mês no Estudaqui!> 
            Você deu um <importante> passo ao <sucesso nos seus estudos>.
            Talves, agora, chegou a hora de aproveitar o <potencial máximo> que o <Estudaqui> pode lhe <proporcionar.>
            Aproveite nossa oferta relâmpago para você:
            <20% de desconto> no plano Anual e libere todas as funcionalidades do aplicativo.
            Atenção: Essa oferta <termina> ao <fechar> essa janela!
            Não perca essa chance de turbinar seus resultados!
        `,
      alertOfferDismiss: true,
      isDismissable: false,
      sendOpenBtnEvent: 'POPUP_offer_first_month_cta_clicked'
    };
    this.openHelper(prop);
    this.analyticsService.logEvent(AnalyticsEventEnum.POPUP_OFFER_FIRST_MONTH);
  }

  async showEbookModal() {
    try {
      const firstname = this.userStoreService.user?.fullname?.split(' ')[0] || '';
      const prop = {
        title: `Parabéns pelo seu esforço!`,
        offerURL: 'https://app.estudaqui.com/assets/ebook/como_estudar_para_provas%20dificeis_ebook_estudaqui.pdf',
        youtubeTextHTML: 'Baixar eBook',
        message: `
            <${firstname}>, percebemos sua dedicação nos estudos e queremos <contribuir> para o seu sucesso: 
            Acesse gratuitamente o ebook <Como Estudar Para Provas Difíceis>.
            Nesse ebook, você encontrará dicas valiosas como:  
            • Aprenda sobre <Ciclo de Estudos>  
            • <Estudo ativo x passivo>  
            • Importância de <Revisão e Resumos>  
            • Importância da <alimentação, sono e exercícios físicos>  
            • <Resolução de questões>
            • Mapa dos Sonhos
            • <Mito das 10-12 horas de estudo>
            E muito mais!
        `,
        isDismissable: false
      };
      this.openHelper(prop);
      this.storageService.set(ONBOARDING_NOTIFICAITON_GIFTED, true);
    } catch (e) {}
  }

  async resolveBookGift() {
    const hasAccountPassed30Days = this.hasAccountPassed30Days(this.userStoreService.user.createdAt);

    if (hasAccountPassed30Days) {
      return;
    }
  }

  async ebookWasGifted() {
    const value = await this.storageService.get(ONBOARDING_NOTIFICAITON_GIFTED);
    return value;
  }

  async openHelper(componentProps: any) {
    const modal = await this.modalCtrl.create({
      component: OfflineTutorialsPage,
      componentProps,
      cssClass: 'sd modal-transparent'
    });

    await modal.present();

    return modal;
  }

  private showModalByNotificationId(id: number) {
    if (id === 2001) {
      this.reviewModal();
      this.markModalAsShown(2001);
    } else if (id === 2002) {
      this.cycleOrScheduleModal();
      this.markModalAsShown(2002);
    } else if (id === 2003) {
      this.firstWeek();
      this.markModalAsShown(2003);
    } else if (id === 2004) {
      this.firstMonth();
      this.markModalAsShown(2004);
    }
  }

  async markModalAsShown(id: number) {
    const notificationsState = await this.fetchNotificationsState();
    if (!notificationsState) return;

    const item = notificationsState.find((n) => n.id === id);

    if (!item) {
      console.warn(`Notificação com ID ${id} não encontrada.`);
      return;
    }

    item.displayedAt = new Date();

    await this.saveNotifcationState(notificationsState);
  }

  private createNotificationsFromUserCreatedAt(userCreatedAt: Date) {
    const rs = [
      {
        id: 2001,
        title: 'Revisões!',
        text: 'Já programou suas revisões?',
        trigger: { at: this.addDaysAndSetTo9AM(userCreatedAt, 1) }
      },
      {
        id: 2002,
        title: 'Ciclou ou Agenda?',
        text: 'Qual método é melhor para você ?',
        trigger: { at: this.addDaysAndSetTo9AM(userCreatedAt, 2) }
      },
      {
        id: 2003,
        title: 'Resumo!',
        text: 'Como foi sua primeira Semana?.',
        trigger: { at: this.addDaysAndSetTo9AM(userCreatedAt, 7) }
      },
      {
        id: 2004,
        title: 'Presente',
        text: 'Você completou 1 Mês de Estudaqui!',
        trigger: { at: this.addDaysAndSetTo9AM(userCreatedAt, 30) }
      }
    ];

    return rs;
  }

  private hasAccountPassed30Days(accountCreatedAt) {
    const createdAt = moment(accountCreatedAt);
    const now = moment();
    const res = now.isAfter(createdAt.add(30, 'days'), 'day');
    console.log({ res, createdAt: createdAt.toDate(), accountCreatedAt });

    return res;
  }

  private addDaysAndSetTo9AM(baseDate: Date, daysOffset: number): Date {
    return moment(baseDate).add(daysOffset, 'days').hour(9).minute(0).second(0).millisecond(0).toDate();
  }

  private isSameDayOrAfter(date1, date2) {
    const d1 = moment(date1);
    const d2 = moment(date2);
    const r = d1.isSame(d2, 'day') || d1.isAfter(d2, 'day');
    return r;
  }

  private mountOTOofferLink(medium: string): string {
    const userEmail = this.userStoreService.user.email;
    const userObjectId = this.userStoreService.user.objectId;

    const urlBase = 'https://app.estudaqui.com/assinar';

    const url = new URL(urlBase);
    url.searchParams.append('email', userEmail);
    url.searchParams.append('userId', userObjectId);
    url.searchParams.append('plano', 'anual_com_desconto');
    url.searchParams.append('utm_source', 'app_estudaqui');
    url.searchParams.append('utm_medium', medium); // ajuste conforme o local da promoção
    url.searchParams.append('utm_campaign', environment.appVersionCode.toString());
    url.searchParams.append('utm_content', 'versao_a');

    return url.toString();
  }
}
